import type { TravelLog, ItemKind, MediaItem } from './types'
import { state } from './state'
import { getMedia } from './storage'
import { t } from './i18n'

export function inferMediaType(url: string): string {
  if (/\.(mp4|webm|mov)(\?|#|$)/i.test(url)) return 'video/external'
  // if (/\.(jpg|jpeg|png|webp|gif)(\?|#|$)/i.test(url))
  return 'image/external'
}

export function _isInstagramUrl(url: string): boolean {
  try {
    return new URL(url).hostname.includes('instagram.com')
  } catch {
    return false
  }
}

export function _toInstagramEmbedUrl(url: string): string {
  const parsed = new URL(url)
  const cleanPath = parsed.pathname
    .replace(/\/embed\/?$/, '')
    .replace(/\/$/, '')
  return `${parsed.origin}${cleanPath}/embed`
}

/** Drop any cached blob URLs for the given blobIds (called after edit/delete). */
export function revokeCachedMedia(blobIds: Iterable<string>): void {
  for (const blobId of blobIds) {
    if (!blobId) continue
    const cached = state.mediaUrls.get(blobId)
    if (cached?.startsWith('blob:')) URL.revokeObjectURL(cached)
    state.mediaUrls.delete(blobId)
  }
}

function _createMediaElement(media: MediaItem, url: string, title: string): HTMLElement {
  if (media.source === 'url' && _isInstagramUrl(url)) {
    const iframe = document.createElement('iframe')
    iframe.src = _toInstagramEmbedUrl(url)
    iframe.loading = 'lazy'
    iframe.referrerPolicy = 'no-referrer-when-downgrade'
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture'
    iframe.className = 'media-embed'
    return iframe
  }

  const isVideo =
    media.type.startsWith('video/') ||
    /\.(mp4|webm|mov)(\?|#|$)/i.test(url)

  if (isVideo) {
    const video = document.createElement('video')
    video.src = url
    video.controls = true
    video.playsInline = true
    return video
  }

  const image = document.createElement('img')
  image.src = url
  image.alt = title
  image.loading = 'lazy'
  return image
}

/** Best displayable URL for a single MediaItem (caches blob URLs by blobId). */
async function _resolveMediaUrl(media: MediaItem): Promise<string> {
  if (media.url && media.source === 'url') return media.url

  if (!media.blobId) return ''

  const cached = state.mediaUrls.get(media.blobId)
  if (cached) return cached

  const blob = await getMedia(media.blobId)
  if (!blob) return ''

  const url = URL.createObjectURL(blob)
  state.mediaUrls.set(media.blobId, url)
  return url
}

/**
 * Render every MediaItem on `item` into `container` as a horizontal gallery.
 * `className` is added to each element so the popup vs side-card variants
 * can be styled independently.
 */
export async function renderMedia(
  container: HTMLElement,
  item: TravelLog,
  className = '',
): Promise<void> {
  container.replaceChildren()

  if (!item.media.length) {
    container.textContent = t('mediaUnavailable')
    return
  }

  const gallery = document.createElement('div')
  gallery.className = 'media-gallery'
  if (item.media.length === 1) gallery.classList.add('media-gallery--single')

  for (const media of item.media) {
    const url = await _resolveMediaUrl(media)

    if (!url) {
      const placeholder = document.createElement('div')
      placeholder.className = 'media-unavailable'
      placeholder.textContent = t('mediaUnavailable')
      gallery.append(placeholder)
      continue
    }

    const element = _createMediaElement(media, url, item.title)
    if (className) element.classList.add(className)
    element.classList.add('media-gallery-item')
    gallery.append(element)
  }

  container.append(gallery)
}
